import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DateWindow } from "@nexus/contracts";
import {
  dashboardCreativesQueryOptions,
  type CompetitorDashboardFilters,
  type CompetitorFiltersInput,
} from "@/lib/queries/dashboard.queries";
import type { DrilldownQuerySection } from "@/lib/queries/drilldown.queries";
import type { CompetitorFilters, MyBrandFilters } from "../../types/dashboard.types";
import { useDashboardAsyncResource } from "../../hooks/useDashboardAsyncResource";
import { useDashboardFiltersStore } from "../../stores/dashboardFilters.store";
import { deriveCardState } from "../../lib/card-state";
import {
  buildCreativeRowDrilldownSearch,
  buildCreativeRowRequest,
  buildDashboardDateReturn,
  type CompetitorCreativeRow as CompetitorCreativeRowConfig,
} from "../../lib/competitor-creative-rows";
import { CreativeRow } from "./CreativeRow";

interface CompetitorCreativeRowProps {
  row: CompetitorCreativeRowConfig;
  tenantId: string | null;
  filters: CompetitorDashboardFilters;
  headerWindow: DateWindow;
  appliedCompetitor: CompetitorFilters;
  appliedMyBrand: MyBrandFilters;
  /** Page reset key (tenant, mode, filters); each row polls its own job under it. */
  resetKey: string;
  /** The applied filters lack a brand or competitor: send nothing and say so. */
  isBlocked: boolean;
}

/** One competitor creative row: its own Drilldown job, then the shared row UI. */
export function CompetitorCreativeRow({
  row,
  tenantId,
  filters,
  headerWindow,
  appliedCompetitor,
  appliedMyBrand,
  resetKey,
  isBlocked,
}: CompetitorCreativeRowProps) {
  const { t } = useTranslation();
  const setDashboardDateReturn = useDashboardFiltersStore((s) => s.setDashboardDateReturn);
  const { startDate: headerStart, endDate: headerEnd } = headerWindow;
  const request = useMemo(
    () => buildCreativeRowRequest(row, filters.dateRange, { startDate: headerStart, endDate: headerEnd }),
    [row, filters.dateRange, headerStart, headerEnd],
  );
  const competitorFilters: CompetitorFiltersInput = {
    myIndustry: filters.myIndustry,
    myBrand: filters.myBrand,
    industryProducts: filters.industryProducts,
    competitorsIndustry: filters.competitorsIndustry,
    mainCompetitor: filters.mainCompetitor,
    benchmark: filters.benchmark,
    platforms: filters.platforms,
    dateRange: request.dateRange,
  };

  const requestTenantId = isBlocked ? null : tenantId;
  const options = dashboardCreativesQueryOptions(requestTenantId, competitorFilters, request);
  const { data: summary } = useQuery(options);
  const resource = useDashboardAsyncResource<DrilldownQuerySection>({
    summary,
    targetKey: options.queryKey,
    resultField: "section",
    resetKey: `${resetKey}:${row.id}`,
    enabled: !!requestTenantId,
  });

  const drilldownSearch = buildCreativeRowDrilldownSearch(row, request, appliedCompetitor, appliedMyBrand);
  const dateReturn = buildDashboardDateReturn(row, drilldownSearch, appliedCompetitor, appliedMyBrand);

  return (
    <CreativeRow
      title={t(`dashboard.creativeRows.${row.id}.title`)}
      subtitle={t(`dashboard.creativeRows.${row.id}.subtitle`)}
      state={isBlocked ? "blocked" : deriveCardState(resource.timedOut, resource.status, resource.data)}
      rows={resource.data?.rows ?? []}
      creativeWindow={request}
      drilldownSearch={drilldownSearch}
      onOpenDrilldown={dateReturn ? () => setDashboardDateReturn(dateReturn) : undefined}
      resetKey={resetKey}
    />
  );
}
